import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Plus, MapPin, Truck, X, Store } from 'lucide-react';
import {
  fetchZoneById,
  removePostcodeFromZone,
  assignPostcodesToZone,
  fetchUnassignedPostcodes,
  fetchRetailerLocationsByZone,
  fetchRetailersByCountry,
  createRetailerLocation,
  deleteRetailerLocation,
} from '../services/api.ts';
import './Zones.css';

const ZoneDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [zone, setZone] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [unassignedPostcodes, setUnassignedPostcodes] = useState<any[]>([]);
  const [selectedPostcodes, setSelectedPostcodes] = useState<number[]>([]);
  const [assigning, setAssigning] = useState(false);

  // Retailer locations state
  const [retailerLocations, setRetailerLocations] = useState<any[]>([]);
  const [showRetailerModal, setShowRetailerModal] = useState(false);
  const [retailers, setRetailers] = useState<any[]>([]);
  const [selectedRetailerId, setSelectedRetailerId] = useState<number | ''>('');
  const [locationAddress, setLocationAddress] = useState('');
  const [locationLat, setLocationLat] = useState('');
  const [locationLng, setLocationLng] = useState('');
  const [addingLocation, setAddingLocation] = useState(false);

  const loadZone = async () => {
    try {
      setLoading(true);
      const data = await fetchZoneById(Number(id));
      setZone(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load zone');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadZone(); }, [id]);

  const loadRetailerLocations = async () => {
    try {
      const data = await fetchRetailerLocationsByZone(Number(id));
      setRetailerLocations(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadRetailerLocations(); }, [id]);

  const handleRemovePostcode = async (postcodeId: number) => {
    if (!window.confirm('Remove this postcode from zone?')) return;
    try {
      await removePostcodeFromZone(Number(id), postcodeId);
      loadZone();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to remove postcode');
    }
  };

  const openAssignModal = async () => {
    try {
      const list = await fetchUnassignedPostcodes(zone?.country?.id);
      setUnassignedPostcodes(list);
      setSelectedPostcodes([]);
      setShowAssignModal(true);
    } catch (err) {
      alert('Failed to load unassigned postcodes');
    }
  };

  const handleAssign = async () => {
    if (selectedPostcodes.length === 0) return;
    setAssigning(true);
    try {
      await assignPostcodesToZone(Number(id), selectedPostcodes);
      setShowAssignModal(false);
      loadZone();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to assign postcodes');
    } finally {
      setAssigning(false);
    }
  };

  const togglePostcodeSelection = (pcId: number) => {
    setSelectedPostcodes(prev =>
      prev.includes(pcId) ? prev.filter(x => x !== pcId) : [...prev, pcId]
    );
  };

  const openRetailerModal = async () => {
    try {
      const list = await fetchRetailersByCountry(zone?.country?.id);
      // Filter out retailers that already have a location in this zone
      const existingIds = retailerLocations.map((rl: any) => rl.retailer_id);
      setRetailers(list.filter((r: any) => !existingIds.includes(r.id)));
      setSelectedRetailerId('');
      setLocationAddress('');
      setLocationLat('');
      setLocationLng('');
      setShowRetailerModal(true);
    } catch {
      alert('Failed to load retailers');
    }
  };

  const handleAddRetailerLocation = async () => {
    if (!selectedRetailerId || !locationAddress || !locationLat || !locationLng) return;
    setAddingLocation(true);
    try {
      await createRetailerLocation({
        retailer_id: Number(selectedRetailerId),
        zone_id: Number(id),
        address: locationAddress,
        latitude: parseFloat(locationLat),
        longitude: parseFloat(locationLng),
      });
      setShowRetailerModal(false);
      loadRetailerLocations();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to add retailer location');
    } finally {
      setAddingLocation(false);
    }
  };

  const handleDeleteRetailerLocation = async (locationId: number) => {
    if (!window.confirm('Remove this retailer location?')) return;
    try {
      await deleteRetailerLocation(locationId);
      loadRetailerLocations();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to remove location');
    }
  };

  if (loading) {
    return (
      <div className="zones-page">
        <div className="zones-loading"><div className="zones-spinner" /><p>Loading zone...</p></div>
      </div>
    );
  }
  if (error) return <div className="zones-page"><div className="zones-error">{error}</div></div>;
  if (!zone) return null;

  return (
    <div className="zones-page">
      <div className="zones-header">
        <button className="zones-btn zones-btn-secondary" onClick={() => navigate('/dashboard/zones')}>
          <ArrowLeft size={16} /> Back to Zones
        </button>
      </div>

      <div className="zones-detail-grid">
        <div className="zones-card">
          <h2>{zone.name}</h2>
          <p className="zones-card-desc">{zone.description || 'No description'}</p>
          <div className="zones-card-info">
            <div><strong>Status:</strong> {zone.is_active ? <span className="zones-badge zones-badge-success">Active</span> : <span className="zones-badge zones-badge-danger">Inactive</span>}</div>
            <div><strong>Delivery Fee:</strong> {zone.delivery_fee > 0 ? <span>${Number(zone.delivery_fee).toFixed(2)}</span> : <span className="zones-badge zones-badge-success">Free</span>}</div>
            <div><strong>Surcharge:</strong> {zone.surcharge_percentage > 0 ? <span>{Number(zone.surcharge_percentage).toFixed(2)}%</span> : <span className="zones-badge zones-badge-success">None</span>}</div>
            <div><strong>Manager:</strong> {zone.manager?.name || 'None assigned'}</div>
            <div><strong>State:</strong> {zone.state?.name || '\u2014'}</div>
            <div><strong>Country:</strong> {zone.country?.name || '\u2014'}</div>
            <div><strong>Created:</strong> {zone.created_at ? new Date(zone.created_at).toLocaleDateString() : '\u2014'}</div>
          </div>
        </div>

        <div className="zones-card">
          <h3>Summary</h3>
          <div className="zones-stat-grid">
            <div className="zones-stat-box zones-stat-box-blue">
              <MapPin size={24} />
              <div className="zones-stat-value">{zone.postcodes?.length ?? 0}</div>
              <div className="zones-stat-label">Postcodes</div>
            </div>
            <div className="zones-stat-box zones-stat-box-green">
              <Truck size={24} />
              <div className="zones-stat-value">{zone.drivers?.length ?? 0}</div>
              <div className="zones-stat-label">Drivers</div>
            </div>
            <div className="zones-stat-box zones-stat-box-blue">
              <Store size={24} />
              <div className="zones-stat-value">{retailerLocations.length}</div>
              <div className="zones-stat-label">Retailers</div>
            </div>
          </div>
        </div>
      </div>

      {/* Postcodes Table */}
      <div className="zones-card" style={{ marginBottom: '24px' }}>
        <div className="zones-header" style={{ marginBottom: '16px', padding: 0 }}>
          <div className="zones-header-left">
            <MapPin size={18} />
            <h3 style={{ margin: 0 }}>Postcodes</h3>
          </div>
          <button className="zones-btn zones-btn-primary" onClick={openAssignModal}>
            <Plus size={16} /> Assign Postcodes
          </button>
        </div>
        <div className="zones-table-wrap">
          <table className="zones-table">
            <thead>
              <tr>
                <th>Postcode</th>
                <th>Town</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(!zone.postcodes || zone.postcodes.length === 0) ? (
                <tr><td colSpan={4} className="zones-empty">No postcodes assigned</td></tr>
              ) : zone.postcodes.map((p: any) => (
                <tr key={p.id}>
                  <td className="zones-cell-name">{p.postcode}</td>
                  <td>{p.town || '\u2014'}</td>
                  <td>{p.is_active ? <span className="zones-badge zones-badge-success">Active</span> : <span className="zones-badge zones-badge-danger">Inactive</span>}</td>
                  <td>
                    <button className="zones-action-btn zones-action-delete" title="Remove" onClick={() => handleRemovePostcode(p.id)}>
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drivers Table */}
      <div className="zones-card">
        <div className="zones-header-left" style={{ marginBottom: '16px' }}>
          <Truck size={18} />
          <h3 style={{ margin: 0 }}>Drivers</h3>
        </div>
        <div className="zones-table-wrap">
          <table className="zones-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Vehicle</th>
                <th>Active</th>
                <th>Available</th>
              </tr>
            </thead>
            <tbody>
              {(!zone.drivers || zone.drivers.length === 0) ? (
                <tr><td colSpan={6} className="zones-empty">No drivers in this zone</td></tr>
              ) : zone.drivers.map((d: any) => (
                <tr key={d.id}>
                  <td className="zones-cell-name">{d.first_name} {d.last_name}</td>
                  <td>{d.email}</td>
                  <td>{d.phone || '\u2014'}</td>
                  <td>{d.vehicle_type ? `${d.vehicle_type} (${d.vehicle_number || '\u2014'})` : '\u2014'}</td>
                  <td>{d.is_active ? <span className="zones-badge zones-badge-success">Yes</span> : <span className="zones-badge zones-badge-danger">No</span>}</td>
                  <td>{d.is_available ? <span className="zones-badge zones-badge-success">Yes</span> : <span className="zones-badge zones-badge-danger">No</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Retailer Locations Table */}
      <div className="zones-card" style={{ marginTop: '24px' }}>
        <div className="zones-header" style={{ marginBottom: '16px', padding: 0 }}>
          <div className="zones-header-left">
            <Store size={18} />
            <h3 style={{ margin: 0 }}>Retailer Locations</h3>
          </div>
          <button className="zones-btn zones-btn-primary" onClick={openRetailerModal}>
            <Plus size={16} /> Add Retailer
          </button>
        </div>
        <div className="zones-table-wrap">
          <table className="zones-table">
            <thead>
              <tr>
                <th>Retailer</th>
                <th>Address</th>
                <th>Latitude</th>
                <th>Longitude</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {retailerLocations.length === 0 ? (
                <tr><td colSpan={5} className="zones-empty">No retailer locations in this zone</td></tr>
              ) : retailerLocations.map((rl: any) => (
                <tr key={rl.id}>
                  <td className="zones-cell-name">{rl.retailer_name}</td>
                  <td>{rl.address}</td>
                  <td>{rl.latitude?.toFixed(6)}</td>
                  <td>{rl.longitude?.toFixed(6)}</td>
                  <td>
                    <button className="zones-action-btn zones-action-delete" title="Remove" onClick={() => handleDeleteRetailerLocation(rl.id)}>
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assign Postcodes Modal */}
      {showAssignModal && (
        <div className="zones-modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="zones-modal zones-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="zones-modal-header">
              <h2>Assign Postcodes to Zone</h2>
              <button className="zones-modal-close" onClick={() => setShowAssignModal(false)}><X size={20} /></button>
            </div>
            <div className="zones-modal-body" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
              {unassignedPostcodes.length === 0 ? (
                <p className="zones-empty" style={{ padding: '24px', textAlign: 'center' }}>No unassigned postcodes available</p>
              ) : (
                <div className="zones-assign-list">
                  {unassignedPostcodes.map((p: any) => (
                    <label key={p.id} className={`zones-assign-item ${selectedPostcodes.includes(p.id) ? 'zones-assign-item-selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={selectedPostcodes.includes(p.id)}
                        onChange={() => togglePostcodeSelection(p.id)}
                      />
                      <strong>{p.postcode}</strong> {'\u2014'} {p.town || 'Unknown'}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="zones-modal-footer">
              <span style={{ marginRight: 'auto', color: '#666', fontSize: '14px' }}>{selectedPostcodes.length} selected</span>
              <button className="zones-btn zones-btn-secondary" onClick={() => setShowAssignModal(false)}>Cancel</button>
              <button className="zones-btn zones-btn-primary" onClick={handleAssign} disabled={assigning || selectedPostcodes.length === 0}>
                {assigning ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Retailer Location Modal */}
      {showRetailerModal && (
        <div className="zones-modal-overlay" onClick={() => setShowRetailerModal(false)}>
          <div className="zones-modal" onClick={e => e.stopPropagation()}>
            <div className="zones-modal-header">
              <h2>Add Retailer Location</h2>
              <button className="zones-modal-close" onClick={() => setShowRetailerModal(false)}><X size={20} /></button>
            </div>
            <div className="zones-modal-body">
              {retailers.length === 0 ? (
                <p className="zones-empty" style={{ padding: '24px', textAlign: 'center' }}>All retailers already have locations in this zone</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}>Retailer</label>
                    <select
                      value={selectedRetailerId}
                      onChange={e => setSelectedRetailerId(e.target.value ? Number(e.target.value) : '')}
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
                    >
                      <option value="">Select a retailer</option>
                      {retailers.map((r: any) => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}>Store Address</label>
                    <input
                      type="text"
                      placeholder="e.g. 123 Main St, Melbourne VIC 3000"
                      value={locationAddress}
                      onChange={e => setLocationAddress(e.target.value)}
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}>Latitude</label>
                      <input
                        type="number"
                        step="any"
                        placeholder="-37.8136"
                        value={locationLat}
                        onChange={e => setLocationLat(e.target.value)}
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}>Longitude</label>
                      <input
                        type="number"
                        step="any"
                        placeholder="144.9631"
                        value={locationLng}
                        onChange={e => setLocationLng(e.target.value)}
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                  <p style={{ fontSize: '13px', color: '#888', margin: 0 }}>
                    Tip: Right-click a location on Google Maps and copy the coordinates.
                  </p>
                </div>
              )}
            </div>
            <div className="zones-modal-footer">
              <button className="zones-btn zones-btn-secondary" onClick={() => setShowRetailerModal(false)}>Cancel</button>
              <button
                className="zones-btn zones-btn-primary"
                onClick={handleAddRetailerLocation}
                disabled={addingLocation || !selectedRetailerId || !locationAddress || !locationLat || !locationLng}
              >
                {addingLocation ? 'Adding...' : 'Add Location'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZoneDetail;
